import { NavLink, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Home, Users, Package, Building2, Settings, LogOut, FileText, Sparkles, Shield, Globe, Contact, UserCheck, Megaphone, Euro, Trophy, Zap, ClipboardCheck, Bell } from 'lucide-react';
import { useVocabulary } from '../context/VocabularyContext';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { cn } from '../lib/utils';
import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { calculateFreeDate, getChurnStatus, getContractBaseDate } from '../utils/churnCalculator';
import { useNotifications } from '../context/NotificationContext';
import NotificationBell from './ui/NotificationBell';
import MandatoOnetWizard from './mandate/MandatoOnetWizard';

const Sidebar = ({ demoView, onClose, mini = false }) => {
    const { t } = useVocabulary();
    const { profile, signOut, isAgent, isDemoAccount, isAdmin, impersonating, stopImpersonation } = useAuth();
    const { theme, toggleTheme } = useTheme();
    const navigate = useNavigate();

    const organization = profile?.organizations;
    const isOnetPlan   = organization?.subscription_plan === 'onet_mandate';

    const [showMandateWizard, setShowMandateWizard] = useState(false);
    const { unread: unreadNotifications } = useNotifications();

    // Track free contracts count for badge
    const [freeContractsCount, setFreeContractsCount] = useState(0);
    const [latestVersion, setLatestVersion] = useState('1.0');
    const [pendingCensimenti, setPendingCensimenti] = useState(0);

    useEffect(() => {
        const fetchFreeContracts = async () => {
            if (!profile?.organization_id) return;

            try {
                const { data: items, error } = await supabase
                    .from('contracts')
                    .select('id, created_at, supplier:suppliers(custom_data)')
                    .eq('organization_id', profile.organization_id);

                if (error) throw error;
                if (!items) return;

                const freeCount = items.filter(item => {
                    const clawbackMonths = item.supplier?.custom_data?.clawback_months || 0;
                    const baseDate = getContractBaseDate(item);
                    const freeDate = calculateFreeDate(baseDate, clawbackMonths);
                    const status = getChurnStatus(freeDate);
                    return status.status === 'free';
                }).length;

                setFreeContractsCount(freeCount);
            } catch (error) {
                console.error('Error fetching free contracts:', error);
            }
        };

        fetchFreeContracts();
        const interval = setInterval(fetchFreeContracts, 60000);
        return () => clearInterval(interval);
    }, [profile?.organization_id]);

    // Fetch latest version from changelogs table
    useEffect(() => {
        supabase
            .from('changelogs')
            .select('version')
            .eq('status', 'released')
            .order('release_date', { ascending: false })
            .limit(1)
            .single()
            .then(({ data }) => { if (data?.version) setLatestVersion(data.version); });
    }, []);

    // Pending censimenti badge for manager/admin roles
    useEffect(() => {
        const role = profile?.role;
        if (role !== 'manager' && role !== 'admin') return;
        if (!profile?.id) return;
        supabase
            .from('agent_censimenti')
            .select('id', { count: 'exact', head: true })
            .eq('status', 'pending')
            .then(({ count }) => setPendingCensimenti(count || 0));
    }, [profile?.id, profile?.role]);

    // Get organization settings
    const orgSettings = organization?.settings || {};
    const industry = orgSettings.industry || 'generic';
    const orgType = orgSettings.type || 'business';

    // Dynamic icon for Items/Contracts based on industry
    // In demo mode always use FileText to match the "official" contracts icon
    const getItemsIcon = () => {
        if (isDemoAccount) return FileText;
        return industry === 'energy' ? FileText : Package;
    };

    const menuItems = [
        { icon: Home, label: 'Dashboard', path: '/app/dashboard' },
        { icon: Users, label: t('clients', 'Clienti'), path: '/app/clients' },
        { icon: Building2, label: t('suppliers', 'Fornitori'), path: '/app/suppliers' },
        { icon: Sparkles, label: 'Comparatore AI', path: '/app/comparatore' },
    ];

    // Add Agenti (Team) link only for agencies with proper role
    const userRole = profile?.role || 'agent';
    // In demo mode mostra il link Team solo quando la vista è 'agency'
    const canAccessTeam = userRole === 'agency' || userRole === 'admin' || userRole === 'manager'
        || (isDemoAccount && demoView === 'agency');

    const isAgencyOrSoloAgent = userRole === 'agency' || profile?.organization?.settings?.type === 'agent';

    const canSeeCompensi = userRole === 'agency' || userRole === 'admin' || userRole === 'soloAgent'
        || (isDemoAccount && (demoView === 'agency' || demoView === 'agent'));

    // Managers and admins can access the Censimenti approval panel
    const canSeeCensimenti = userRole === 'manager' || userRole === 'admin';

    const allMenuItems = isAgent || !canAccessTeam
        ? menuItems
        : [
            ...menuItems.slice(0, 2), // Dashboard, Clienti
            { icon: Contact, label: 'Team', path: '/app/team' },
            ...(canSeeCensimenti ? [{ icon: ClipboardCheck, label: 'Censimenti', path: '/app/censimento', badge: pendingCensimenti || null }] : []),
            ...(isAgencyOrSoloAgent ? [{ icon: Megaphone, label: 'Marketing', path: '/app/marketing' }] : []),
            ...menuItems.slice(2) // Fornitori, Comparatore
        ];

    // Inject Compensi + Championship after Fornitori for eligible roles
    const canSeeChampionship = canSeeCompensi; // stessi ruoli di Compensi
    // CTE di rete: visibile a chi ha un manager (agenti/agenzie di una rete)
    const hasManager = !!profile?.manager_id;

    const finalMenuItems = canSeeCompensi
        ? [
            ...allMenuItems.slice(0, allMenuItems.findIndex(i => i.path === '/app/comparatore')),
            { icon: Euro, label: 'Compensi', path: '/app/compensi' },
            { icon: Trophy, label: 'Championship', path: '/app/championship' },
            ...allMenuItems.slice(allMenuItems.findIndex(i => i.path === '/app/comparatore')),
          ]
        : allMenuItems;

    // Admin shortcut (only for real admins, hidden during impersonation so they don't loop)
    const showAdminLink = isAdmin && !impersonating;

    const handleLogout = async () => {
        await signOut();
        navigate('/login');
    };

    return (
        <>
        <motion.aside
            initial={{ x: -20, opacity: 0 }}
            animate={{ x: 0, opacity: 1, width: mini ? '3.5rem' : '16rem' }}
            transition={{ duration: 0.3 }}
            style={{ overflowX: 'hidden' }}
            className={cn(
                'h-[calc(100vh-2rem)] sticky top-4 m-4 transition-all duration-300 rounded-xl flex flex-col overflow-hidden',
                'bg-white/90 border-gray-200/50',
                'dark:bg-white/5 dark:border-white/10 dark:backdrop-blur-2xl',
                'shadow-2xl',
                mini ? 'w-14' : 'w-64'
            )}
        >
            {/* TOP SECTION: Logo + Notification Bell */}
            <div className="flex items-center p-4 border-b border-gray-200/50 dark:border-white/10">
                {mini
                    ? <img src="/favicon-onet.png" alt="ONET" className="w-8 h-8 object-contain filter drop-shadow mx-auto" />
                    : <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex items-center justify-between w-full">
                        <div className="flex flex-col items-center flex-1">
                            <img src="/logo-onet.png" alt="ONET" className="w-24 h-auto object-contain filter drop-shadow-lg" />
                            <p className="text-xs text-gray-500 dark:text-gray-400 font-medium mt-3">CRM</p>
                        </div>
                    {!isDemoAccount && <div className="flex-shrink-0"><NotificationBell /></div>}
                    </motion.div>
                }
            </div>

            {/* MIDDLE SECTION: Scrollable Navigation */}
            <nav className="flex-1 overflow-y-auto overflow-x-hidden p-2 space-y-1 [&::-webkit-scrollbar]:hidden [scrollbar-width:none]">
                {finalMenuItems.map((item) => (
                    <motion.div
                        key={item.path}
                        whileHover={{ x: mini ? 0 : 2 }}
                        whileTap={{ scale: 0.97 }}
                        transition={{ duration: 0.15 }}
                        className="relative group/nav"
                    >
                        <NavLink
                            to={item.path}
                            onClick={onClose}
                            title={mini ? item.label : undefined}
                            className={({ isActive }) =>
                                cn(
                                    'flex items-center gap-3 rounded-lg transition-all relative',
                                    mini ? 'px-2 py-3 justify-center' : 'px-4 py-3',
                                    isActive
                                        ? 'bg-primary/10 text-primary dark:bg-primary/20 dark:text-violet-400 font-medium'
                                        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100/50 dark:hover:bg-white/5'
                                )
                            }
                        >
                            {({ isActive }) => (
                                <>
                                    {/* Vertical Pill Indicator */}
                                    {isActive && (
                                        <motion.div
                                            layoutId="activeIndicator"
                                            className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 bg-gradient-to-b from-primary to-accent rounded-r-full shadow-lg shadow-primary/50"
                                            transition={{ type: 'spring', bounce: 0.2, duration: 0.6 }}
                                        />
                                    )}
                                    <item.icon
                                        className={cn(
                                            'w-5 h-5 flex-shrink-0 relative z-10 transition-all',
                                            isActive && 'text-primary dark:text-violet-400'
                                        )}
                                    />
                                    {!mini && (
                                        <motion.span
                                            initial={{ opacity: 0 }}
                                            animate={{ opacity: 1 }}
                                            className="relative z-10 flex items-center gap-2 text-sm"
                                        >
                                            {item.label}
                                            {/* Badge for free contracts */}
                                            {(item.label === 'Contratti' || item.label === t('items', 'Items')) && freeContractsCount > 0 && (
                                                <motion.span
                                                    initial={{ scale: 0 }}
                                                    animate={{ scale: 1 }}
                                                    className="flex items-center justify-center min-w-[20px] h-5 px-1.5 bg-red-500 text-white text-xs font-bold rounded-full animate-pulse shadow-lg shadow-red-500/50"
                                                >
                                                    {freeContractsCount}
                                                </motion.span>
                                            )}
                                            {/* Generic badge (e.g. pending censimenti) */}
                                            {item.badge > 0 && (
                                                <motion.span
                                                    initial={{ scale: 0 }}
                                                    animate={{ scale: 1 }}
                                                    className="flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold text-white rounded-full"
                                                    style={{ background: 'linear-gradient(135deg,#f59e0b,#ef4444)', boxShadow: '0 0 6px rgba(245,158,11,0.5)' }}
                                                >
                                                    {item.badge}
                                                </motion.span>
                                            )}
                                        </motion.span>
                                    )}
                                </>
                            )}
                        </NavLink>
                    </motion.div>
                ))}

                {/* Admin Panel shortcut */}
                {showAdminLink && (
                    <div className="mt-2 pt-2 border-t border-gray-200/30 dark:border-white/10 relative group/nav">
                        <NavLink
                            to="/admin/overview"
                            className={({ isActive }) =>
                                cn(
                                    "flex items-center gap-3 rounded-lg transition-all",
                                    mini ? 'px-2 py-3 justify-center' : 'px-4 py-3',
                                    isActive
                                        ? "bg-violet-600/20 text-violet-300 font-medium"
                                        : "text-gray-400 hover:bg-violet-500/10 hover:text-violet-300"
                                )
                            }
                        >
                            <Shield className="w-5 h-5 flex-shrink-0" />
                            {!mini && <span className="text-sm">Admin Panel</span>}
                            {!mini && <span className="ml-auto text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-violet-500/20 text-violet-400 border border-violet-500/30">ADMIN</span>}
                        </NavLink>
                        {mini && (
                            <div className="absolute left-full top-1/2 -translate-y-1/2 ml-2 px-2 py-1 rounded-md text-xs font-semibold bg-gray-900 text-white whitespace-nowrap opacity-0 group-hover/nav:opacity-100 pointer-events-none transition-opacity z-50">
                                Admin Panel
                            </div>
                        )}
                    </div>
                )}

            </nav>

            {/* VERSION BADGE */}
            {!mini && (
                <div className="flex justify-center pb-2 pt-1">
                    <button
                        onClick={() => navigate('/app/changelog')}
                        className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-semibold tracking-widest uppercase bg-gradient-to-r from-primary/10 to-accent/10 border border-primary/20 dark:border-violet-500/30 text-primary dark:text-violet-400 hover:bg-violet-500/20 hover:border-violet-500/50 transition-all select-none cursor-pointer"
                    >
                        <span className="w-1.5 h-1.5 rounded-full bg-primary dark:bg-violet-400 animate-pulse" />
                        Beta v{latestVersion}
                    </button>
                </div>
            )}

            {/* BOTTOM SECTION: Pinned Actions */}
            <div className={cn('border-t border-gray-200/50 dark:border-white/10 bg-gray-50/50 dark:bg-black/20 rounded-b-xl', mini ? 'p-2' : 'p-4 space-y-3')}>
                {mini ? (
                    // Mini: solo avatar cliccabile + logout
                    <div className="flex flex-col items-center gap-2">
                        {profile && (
                            <button onClick={() => navigate('/app/settings')}
                                className="w-9 h-9 rounded-lg bg-gradient-to-br from-primary to-accent flex items-center justify-center text-white font-medium text-sm shadow-violet hover:opacity-90 transition"
                                title={profile.name || profile.email}>
                                {(profile.name?.[0] || profile.email?.[0])?.toUpperCase()}
                            </button>
                        )}
                        <button onClick={handleLogout}
                            className="p-2 rounded-lg text-red-400 hover:bg-red-500/10 transition"
                            title="Logout">
                            <LogOut className="w-4 h-4" />
                        </button>
                    </div>
                ) : (
                    // Full: user profile + logout
                    <>
                        {profile && (
                            <motion.div
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                                onClick={() => navigate('/app/settings')}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => e.key === 'Enter' && navigate('/app/settings')}
                                className="w-full flex items-center gap-3 px-3 py-2 rounded-lg bg-white/60 dark:bg-white/5 border border-gray-200/50 dark:border-white/10 hover:bg-white/80 dark:hover:bg-white/10 transition-all cursor-pointer"
                            >
                                <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-primary to-accent flex items-center justify-center text-white font-medium text-sm shadow-violet flex-shrink-0">
                                    {(profile.name?.[0] || profile.email?.[0])?.toUpperCase()}
                                </div>
                                <div className="flex-1 min-w-0 text-left">
                                    <p className="text-xs font-medium text-gray-800 dark:text-gray-200 truncate">
                                        {profile.role === 'agency'
                                            ? (organization?.name || 'Workspace')
                                            : (profile.name?.split(' ')[0] || profile.email?.split('@')[0] || 'Utente')}
                                    </p>
                                    <p className="text-[10px] text-gray-500 dark:text-gray-500 truncate capitalize">
                                        {profile.role === 'admin' ? 'Amministratore' : profile.role === 'manager' ? 'Manager' : profile.role === 'agency' ? 'Agenzia' : profile.role === 'soloAgent' ? 'Agente Indipendente' : 'Agente'}
                                        {organization?.name && profile.role !== 'soloAgent' && profile.role !== 'agency' && profile.role !== 'admin' ? ` in ${organization.name}` : ''}
                                    </p>
                                    {/* ── Plan Pill — sotto il ruolo ── */}
                                    {!isDemoAccount && organization?.subscription_plan && (
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                isOnetPlan
                                                    ? navigate('/app/settings?tab=billing')
                                                    : setShowMandateWizard(true);
                                            }}
                                            className={`mt-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-semibold transition-colors ${
                                                isOnetPlan
                                                    ? 'text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-500/15 hover:bg-emerald-100 dark:hover:bg-emerald-500/25'
                                                    : 'text-violet-600 dark:text-violet-400 bg-violet-50 dark:bg-violet-500/15 hover:bg-violet-100 dark:hover:bg-violet-500/25'
                                            }`}
                                        >
                                            <Zap className="w-2.5 h-2.5" />
                                            {isOnetPlan ? 'Piano ONET · Gratuito' : 'Piano PRO'}
                                        </button>
                                    )}
                                </div>
                                <Settings className="w-5 h-5 text-gray-600 dark:text-gray-400 flex-shrink-0" />
                            </motion.div>
                        )}

                        <button
                            onClick={handleLogout}
                            className={cn(
                                'w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg transition-all',
                                'bg-red-50 dark:bg-red-950/20 border border-red-200/50 dark:border-red-500/20',
                                'hover:bg-red-100 dark:hover:bg-red-950/40',
                                'text-red-700 dark:text-red-400 text-sm font-medium'
                            )}
                        >
                            <LogOut className="w-4 h-4" />
                            <span>Logout</span>
                        </button>
                    </>
                )}
            </div>
        </motion.aside>

        {/* Mandate ONET Wizard — opened when clicking PRO pill */}
        <MandatoOnetWizard
            isOpen={showMandateWizard}
            onClose={() => setShowMandateWizard(false)}
            onSuccess={() => setShowMandateWizard(false)}
        />
        </>
    );
};

export default Sidebar;

